<template>
    <!-- Order -->
    <nav
        class="flex w-full max-w-5xl flex-col justify-end gap-1 lg:flex-row lg:items-center lg:justify-end"
        aria-label="Pagination"
    >
        <div class="hidden gap-2 px-2 pb-2 text-sm font-medium text-gray-400 select-none lg:flex">
            <MdiSort class="h-5 w-5 flex-none text-gray-400" />
            <div>{{ t('orderBy') }}</div>
        </div>
        <div class="pb-2 text-sm font-medium text-gray-400 select-none lg:hidden">
            <ActionButton
                :value="t('orderBy')"
                state="clean"
                size="md"
                :icon="MdiSort"
                @click="toggleOrderBar()"
            />
        </div>
        <Transition
            enter-active-class="transition-all duration-300 ease-in-out"
            leave-active-class="transition-all duration-300 ease-in-out"
            enter-from-class="opacity-0 translate-y-full"
            enter-to-class="opacity-100 translate-y-0"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-full"
        >
            <div
                v-show="orderBarVisible || isLargeScreen"
                :class="[
                    'flex flex-col justify-end gap-2 pb-2 lg:flex-row lg:items-center lg:justify-end',
                ]"
            >
                <div
                    v-for="orderOption in orderOptions"
                    :key="orderOption.value"
                    class="select-none"
                >
                    <router-link
                        :to="getRouteWithSort(orderOption)"
                        :class="[
                            'text-gray-500 hover:border-gray-300 hover:text-gray-700',
                            'flex items-center border-b-2 border-transparent px-2 py-2 text-sm font-medium select-none',
                        ]"
                    >
                        <MdiChevronUp
                            v-if="getDirection(orderOption.value) === 'asc'"
                            class="h-5 w-5 text-blue-500"
                        />
                        <MdiChevronDown
                            v-if="getDirection(orderOption.value) === 'desc'"
                            class="h-5 w-5 text-blue-500"
                        />
                        <MdiMinus
                            v-if="getDirection(orderOption.value) === null"
                            class="h-5 w-5 text-gray-300"
                        />
                        <span
                            :class="[
                                isFieldSorted(current, orderOption.value)
                                    ? 'font-semibold text-blue-600'
                                    : '',
                                'sm:inline',
                            ]"
                            >{{ orderOption.name }}</span
                        >
                    </router-link>
                </div>
            </div>
        </Transition>
    </nav>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { getSortDirection, toggleSort, isFieldSorted } from '../composables/useSortBy';
import ActionButton from './ActionButton.vue';
import { MdiChevronDown, MdiChevronUp, MdiMinus, MdiSort } from 'materialdesignicons-vue3/icons/';

defineExpose({
    name: 'OrderBar',
});

const props = defineProps({
    current: {
        type: Array,
        required: true,
    },
    orderOptions: {
        type: Array,
        required: true,
    },
});

const current = computed(() => props.current);
const orderOptions = computed(() => props.orderOptions);
const route = useRoute();
const orderBarVisible = ref(false);

// Detect if the screen is large (lg breakpoint, e.g., 1024px)
const isLargeScreen = ref(window.matchMedia('(min-width: 1024px)').matches);

window.addEventListener('resize', () => {
    isLargeScreen.value = window.matchMedia('(min-width: 1024px)').matches;
});

const getDirection = (field) => {
    return getSortDirection(current.value, field);
};

const toggleOrderBar = () => {
    orderBarVisible.value = !orderBarVisible.value;
};

const getRouteWithSort = (orderOption) => {
    // Use current (from props) which includes the default value
    const newSortBy = toggleSort(current.value, orderOption.value);
    return {
        name: route.name,
        params: { ...route.params, page: '1' },
        query: {
            ...route.query,
            sortBy: newSortBy.length > 0 ? newSortBy : undefined,
        },
    };
};
</script>
